// packages import
import { Autocomplete, CircularProgress, FormControl, ListItem, TextField } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
// component import
import ListBox from './ListBox'
// others
import { ExternalUser, ExternalUserRoleRole, Maybe, useFetchAllExternalUsersLazyQuery } from '../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../constants'
import { FormErrors, JudgeAutoCompleteProps } from '../../interfaceTypes'
import { ExternalUsersResult } from '../../interfaceTypes/common'

export const JudgeAutoComplete: FC<JudgeAutoCompleteProps> = ({ onChangeHandle, judgesData }) => {
  const [externalUsersData, setExternalUsersData] = useState<ExternalUsersResult>([])
  const [updatedJudgesData, setUpdatedJudgesData] = useState<Array<Maybe<ExternalUser>>>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)
  const {
    control,
    formState: { errors }
  } = useFormContext()
  const customError = errors[`judgeUser`] as FormErrors['judgeUser']

  const [fetchAllExternalUsers, { loading }] = useFetchAllExternalUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onCompleted(data) {
      if (data) {
        const { fetchAllExternalUsers } = data

        if (fetchAllExternalUsers) {
          const { externalUsers, response } = fetchAllExternalUsers
          const { status } = response || {}

          if (status === 200 && externalUsers) {
            setExternalUsersData(prevData => (prevData ? [...prevData, ...externalUsers] : externalUsers))
          }
        }
      }
    }
  })

  useEffect(() => {
    fetchAllExternalUsers({
      variables: {
        externalUsersInput: {
          roles: [ExternalUserRoleRole.Judge],

          paginationOptions: {
            limit: 20,
            page: currentPage
          },
          searchValue: debouncedSearchTerm
        }
      }
    })
    // eslint-disable-next-line
  }, [currentPage, debouncedSearchTerm])

  useEffect(() => {
    setExternalUsersData([])
    setCurrentPage(1)
  }, [debouncedSearchTerm])

  useEffect(() => {
    const addedJudgeIds = externalUsersData
      ?.filter(judge => judgesData?.some(item => item?.id === judge?.id))
      .map(item => item?.id)
    const remainingJudges = externalUsersData?.filter(
      value => !addedJudgeIds?.some(oneDuplicate => oneDuplicate === value?.id)
    )

    setUpdatedJudgesData(remainingJudges ?? [])
  }, [externalUsersData, judgesData])

  return (
    <FormControl variant="outlined" fullWidth>
      <Controller
        name="judgeUser"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            fullWidth
            options={updatedJudgesData || []}
            PaperComponent={ListBox}
            value={value}
            loading={loading}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onChange={(event, item) => {
              onChange(item)
              onChangeHandle && onChangeHandle(item?.id || '')
            }}
            getOptionLabel={item => {
              const { firstName, lastName, displayName, email } = item || {}
              const name = displayName?.trim() || `${firstName || ''} ${lastName || ''}`.trim()
              const options = `${name} ${email ? `(${email})` : ''}`
              return item.id ? options : ''
            }}
            onInputChange={(event, newInputValue) => {
              setSearchTerm(newInputValue)
            }}
            ListboxProps={{
              onScroll: event => {
                const listboxNode = event.currentTarget
                if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
                  setCurrentPage(currentPage + 1)
                }
              },
              role: 'list-box',
              style: {
                maxHeight: '190px'
              }
            }}
            renderOption={(props, option, index) => {
              const key = `listItem-${index}-${option?.id}`
              const { firstName, lastName, displayName, email } = option || {}
              const name = displayName?.trim() || `${firstName || ''} ${lastName || ''}`.trim()

              return (
                <ListItem {...props} key={key}>
                  {`${name} ${email ? `(${email})` : ''}`}
                </ListItem>
              )
            }}
            renderInput={params => (
              <TextField
                {...params}
                error={!!customError?.id?.message}
                helperText={customError?.id?.message || ''}
                label="Select judge"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        )}
      />
    </FormControl>
  )
}
